import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Coins, Calendar, MessageSquare, Check, X, RefreshCw, Eye } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ORESTORE_LOGO = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/3azwctos_Ore-Store-LOGO2.png";

const formatMesos = (n) => {
  const v = Math.floor(Number(n) || 0);
  return v.toLocaleString("en-US").replace(/,/g, ".");
};

const STATUSES = ["pending", "scheduled", "completed", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-orange-500/30 text-orange-100 border-orange-500",
  scheduled: "bg-emerald-500/30 text-emerald-100 border-emerald-500",
  completed: "bg-blue-500/30 text-blue-100 border-blue-500",
  cancelled: "bg-red-500/30 text-red-100 border-red-500",
};

const AdminOreStoreOffers = () => {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [updating, setUpdating] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchOffers = useCallback(async (status = filter) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/orestore/offers`, {
        params: status && status !== "all" ? { status } : {},
        headers: getAuthHeaders(),
      });
      setOffers(res.data.offers || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error("Failed to load OreStore offers");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchOffers(filter);
  }, [filter, fetchOffers]);

  const updateStatus = async (offer, newStatus) => {
    setUpdating(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/orestore/offers/${offer.id}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success(`Offer marked as ${newStatus}`);
      // refresh list
      await fetchOffers(filter);
      // update selected if open
      if (selectedOffer && selectedOffer.id === offer.id) {
        setSelectedOffer(res.data.offer);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <Card className="bg-card/50" data-testid="admin-orestore-offers-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <img src={ORESTORE_LOGO} alt="OreStore" className="h-7 w-auto" />
              <span>OreStore Offers</span>
              {(counts.pending || 0) > 0 && (
                <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {counts.pending} new
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Manage incoming buyback offers — schedule meetings, mark as completed, or cancel.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchOffers(filter)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="all" className="gap-1">
              All <span className="text-xs opacity-70">({totalCount})</span>
            </TabsTrigger>
            {STATUSES.map((s) => (
              <TabsTrigger key={s} value={s} className="gap-1 capitalize">
                {s} <span className="text-xs opacity-70">({counts[s] || 0})</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : offers.length === 0 ? (
            <div className="text-center text-muted-foreground py-12 italic">
              No offers in this filter.
            </div>
          ) : (
            offers.map((offer) => (
              <div
                key={offer.id}
                className="flex flex-wrap md:flex-nowrap items-center gap-3 p-3 rounded-lg border bg-card/30 hover:bg-card/60 transition"
              >
                <div className="text-xs font-mono text-muted-foreground w-16 shrink-0">
                  #{offer.id.slice(0, 6)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm">
                      {offer.seller_username || `Guest_${(offer.guest_id || "").slice(-6) || "anon"}`}
                    </span>
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${STATUS_COLORS[offer.status] || STATUS_COLORS.pending}`}>
                      {offer.status}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {(offer.items || []).length} items
                    </span>
                    {offer.contact_ign && (
                      <span className="text-xs text-amber-400">IGN: {offer.contact_ign}</span>
                    )}
                    {offer.contact_discord && (
                      <span className="text-xs text-indigo-400">{offer.contact_discord}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <Coins className="inline w-3 h-3 mr-1" />
                    <span className="text-yellow-400 font-bold font-mono">{formatMesos(offer.total_payout)}</span>
                    <span className="ml-1">mesos</span>
                    <span className="mx-2">•</span>
                    {new Date(offer.created_at).toLocaleString()}
                    {offer.meeting?.scheduled_at && (
                      <>
                        <span className="mx-2">•</span>
                        <Calendar className="inline w-3 h-3 mr-1" />
                        {offer.meeting.scheduled_at}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setSelectedOffer(offer)}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                  {offer.status !== "completed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-400 hover:bg-emerald-500/20 border-emerald-500/40"
                      onClick={() => updateStatus(offer, "completed")}
                      disabled={updating}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {offer.status !== "cancelled" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-400 hover:bg-red-500/20 border-red-500/40"
                      onClick={() => updateStatus(offer, "cancelled")}
                      disabled={updating}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* Offer detail modal */}
      <Dialog open={!!selectedOffer} onOpenChange={(v) => { if (!v) setSelectedOffer(null); }}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={ORESTORE_LOGO} alt="OreStore" className="h-7 w-auto" />
              Offer #{selectedOffer?.id?.slice(0, 8)}
            </DialogTitle>
            <DialogDescription>
              Status: <span className="capitalize font-bold">{selectedOffer?.status}</span>
            </DialogDescription>
          </DialogHeader>

          {selectedOffer && (
            <div className="space-y-3 overflow-y-auto pr-1 flex-1">
              {/* Seller / contact */}
              <div className="bg-muted/30 rounded p-3 text-sm space-y-1 border">
                <div><strong>Seller:</strong> {selectedOffer.seller_username || `Guest (${(selectedOffer.guest_id || "").slice(-8) || "anon"})`}</div>
                {selectedOffer.contact_ign && <div><strong>IGN:</strong> {selectedOffer.contact_ign}</div>}
                {selectedOffer.contact_discord && <div><strong>Discord:</strong> {selectedOffer.contact_discord}</div>}
                {selectedOffer.notes && (
                  <div className="flex items-start gap-1 mt-2"><MessageSquare className="w-3.5 h-3.5 mt-0.5" /> <span className="italic text-muted-foreground">{selectedOffer.notes}</span></div>
                )}
              </div>

              {/* Items */}
              <div className="bg-muted/30 rounded p-3 border">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-bold">Items</div>
                <div className="space-y-1">
                  {(selectedOffer.breakdown || selectedOffer.items || []).map((b, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {b.image_url && <img src={b.image_url} alt="" className="w-6 h-6" style={{ imageRendering: "pixelated" }} />}
                      <span className="flex-1 font-bold">{b.name}</span>
                      <span className="font-mono text-muted-foreground">×{b.amount}</span>
                      {b.subtotal != null && <span className="font-mono w-32 text-right">{formatMesos(b.subtotal)}</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t text-sm space-y-0.5">
                  <div className="flex justify-between"><span>Base</span><span className="font-mono">{formatMesos(selectedOffer.total_base)}</span></div>
                  <div className="flex justify-between text-fuchsia-400"><span>Bonus ({selectedOffer.bonus_percent}%)</span><span className="font-mono">+{formatMesos(selectedOffer.bonus_amount)}</span></div>
                  <div className="flex justify-between font-extrabold text-yellow-400"><span>Total payout</span><span className="font-mono">{formatMesos(selectedOffer.total_payout)}</span></div>
                </div>
              </div>

              {/* Meeting */}
              {selectedOffer.meeting?.scheduled_at && (
                <div className="bg-emerald-900/30 border-2 border-emerald-500/40 rounded p-3 text-sm space-y-1">
                  <div className="text-emerald-300 font-bold flex items-center gap-1"><Calendar className="w-4 h-4" /> Meeting scheduled</div>
                  <div>🕐 {selectedOffer.meeting.scheduled_at}</div>
                  <div>📍 FM 15 — CH 15</div>
                  {selectedOffer.meeting.notes && <div className="italic">📝 {selectedOffer.meeting.notes}</div>}
                </div>
              )}

              {selectedOffer.admin_handled_by && (
                <div className="text-xs text-muted-foreground">
                  Last updated by <strong>{selectedOffer.admin_handled_by}</strong>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {selectedOffer && STATUSES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={selectedOffer.status === s ? "default" : "outline"}
                disabled={updating || selectedOffer.status === s}
                onClick={() => updateStatus(selectedOffer, s)}
                className="capitalize"
              >
                {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : s}
              </Button>
            ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminOreStoreOffers;
